import "../../../components/MediaChapter/a.scss";
import * as Yup from "yup";
import { AnimatePresence } from "framer-motion";
import { Dropdown, Offcanvas } from "react-bootstrap";
import {
  Scissors,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import SectionHeader from "@/components/Layout/SectionHeader";
import {
  Button,
  ChipInput,
  GeneralPlayer,
  Skeleton,
  TextArea,
  TextInput,
  Tooltip,
  AIAssistButtons,
  toast,
} from "../../../components";
import type { GeneralPlayerHandle, ImageReel } from "@/components/CleoPlayer";
import VideoRangeTrimmer from "@/components/VideoRangeTrimmer/VideoRangeTrimmer";
import {
  getAddChapter,
  getDeleteChapter,
  getListChapters,
  getUpdateChapter,
} from "@/store/media/media.action";
import { useEffect, useRef, useState } from "react";
import { ConnectedProps, connect, useDispatch } from "react-redux";
import AddedChapterThumbnail from "@/components/MediaChapter/AddedChapterThumbnail";
import { RootState } from "@/store/index";
import {
  copyTextToClipboard,
  fileProcessingStatusToTextAndColor,
  secondsToHms,
} from "../../../utils";
import {
  resetMediaChapters,
  setCurrentOpenedChapter,
  setMediaChapters,
} from "@/store/media/media.slice";
import { useFormik } from "formik";
import { ChapterInterface, MediaModel, GetMediaApi } from "@/database";
import ThumbnailSelection from "@/components/ThumbnailSelection/ThumbnailSelection";
import { generateBaseUrl, getThumbnailUrlUtil } from "@/utils/mediaUtils";
import { getAssets, generateClip } from "@/store/media/media.action";
import { updateModal } from "@/store/app/app.slice";
import { aiService } from "@/store/app/app.actions";
import noImage from "../../../assets/icons/svg/no-image.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertDialog } from "@/utils/ui/AlertDialog/AlertDialog";
import { AppDispatch } from "../../../store";

export interface MediaChapterForm {
  title: string;
  description: string;
  start_time: number;
  end_time: number;
  duration: number;
  thumb: string;
  base64: string[];
  id: string;
  thumbId: string;
  clip_id: string | undefined;
  screenshot?: number;
  is_change?: boolean;
  clip_start?: number;
  clip_end?: number;
  tags: string[];
}
const MIN_CLIP_DURATION = 10;

const MediaChapter = ({
  currentMediaDetailsForEdit,
  getDeleteChapter,
  chapters,
  getListChapters,
  resetMediaChapters,
  isSavingChapter,
  getUpdateChapter,
  getAddChapter,
  mediaAssets,
  currentOpenedChapter,
  isLoadingChapters,
  setCurrentOpenedChapter,

  generateClip,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentChapter, setCurrentChapter] = useState<string>("");
  const [tempChapter, setTempChapter] = useState<ChapterInterface | null>(null);
  const [lastTempThumb, setLastTempThumb] = useState<Record<string, string>>(
    {}
  );
  const [hasTimeChanged, setHasTimeChanged] = useState(false);
  const [playerKey, setPlayerKey] = useState(0);
  const [currentPlayerTime, setCurrentPlayerTime] = useState(0);
  const [isPlayerPaused, setIsPlayerPaused] = useState(true);
  const [imageReel, setImageReel] = useState<ImageReel | null>(null);
  const startTrimRef = useRef(currentOpenedChapter?.start_time || 0);
  const endTrimRef = useRef(
    currentOpenedChapter?.end_time || currentMediaDetailsForEdit?.meta?.duration
  );
  const durationRef = useRef(
    currentOpenedChapter?.duration || currentMediaDetailsForEdit?.meta?.duration
  );
  const [childMediaData, setChildMediaData] = useState<MediaModel | null>(null);
  const [isLoadingChildMedia, setIsLoadingChildMedia] = useState(false);
  const [activeField, setActiveField] = useState<
    "title" | "description" | null
  >(null);
  const [customPrompt, setCustomPrompt] = useState({
    title: { open: false, text: "", isSending: false },
    description: { open: false, text: "", isSending: false },
  });
  const [isFormDirty1, setIsFormDirty1] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [deleteChapterId, setDeleteChapterId] = useState<string | null>(null);
  const generalPlayerRef = useRef<GeneralPlayerHandle>(null);

  // Videos for the multi-video trimmer
  const videos = [
    {
      id: currentMediaDetailsForEdit?.id || "1",
      title: currentMediaDetailsForEdit?.title || "Current Video",
      duration: currentMediaDetailsForEdit?.meta?.duration || 0,
    },
    // Add more videos here as needed
  ];

  const formik = useFormik<MediaChapterForm>({
    validateOnChange: true,
    enableReinitialize: false,
    initialValues: {
      title: currentOpenedChapter?.title,
      description: currentOpenedChapter?.description,
      duration:
        currentOpenedChapter?.duration ||
        currentMediaDetailsForEdit?.meta?.duration,
      end_time:
        currentOpenedChapter?.end_time ||
        currentMediaDetailsForEdit?.meta?.duration,
      start_time: currentOpenedChapter?.start_time || 0,
      thumb: currentOpenedChapter
        ? currentOpenedChapter?.thumb
          ? `${currentOpenedChapter?.base}/${currentOpenedChapter?.thumb}${
              currentMediaDetailsForEdit?.source === "dbsync"
                ? ".jpg"
                : "_180.jpg"
            }`
          : getThumbnailUrlUtil({
              media: currentMediaDetailsForEdit,
              id: currentMediaDetailsForEdit?.images?.thumb,
              source: currentMediaDetailsForEdit?.source,
            })
        : generateBaseUrl(currentMediaDetailsForEdit) +
          currentMediaDetailsForEdit?.images?.thumb +
          (currentMediaDetailsForEdit?.source === "dbsync"
            ? ".jpg"
            : "_180.jpg"),
      thumbId: currentOpenedChapter
        ? currentOpenedChapter?.thumb ||
          currentMediaDetailsForEdit?.images?.thumb
        : currentMediaDetailsForEdit?.images?.thumb,
      base64: currentOpenedChapter?.base64 || [],
      id: currentOpenedChapter?.id || "",
      clip_id: currentOpenedChapter?.clip_id || undefined,
      screenshot: currentOpenedChapter?.screenshot,
      clip_start: currentOpenedChapter?.clip_start,
      clip_end: currentOpenedChapter?.clip_end,
      tags: currentOpenedChapter?.tags || [],
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .min(3, "Title should be more than 3 charcters")
        .max(100, "title should be less than 100 characters")
        .required("This field is required"),
      description: Yup.string()
        .min(5, "Description should be more than 5 charcters")
        .max(5000, "Description should be less than 5000 characters")
        .required("This field is required"),
    }),
    onSubmit: (values) => {
      const chapterData = {
        title: values.title,
        description: values.description,
        tags: (values.tags ?? []).join(","),
        duration: Number(durationRef.current),
        start_time: Number(startTrimRef.current),
        end_time: Number(endTrimRef.current),
        mediaId: currentMediaDetailsForEdit.id,
        ...(values.base64?.length > 0
          ? { screenshot: values.screenshot }
          : {
              thumb: values.thumbId,
              base: currentMediaDetailsForEdit?.images?.base,
            }),
      };

      if (currentChapter === "new_chapter") {
        getAddChapter(
          { ...chapterData },
          (response: {
            data: { id: string; clip_id?: string; enable?: boolean };
          }) => {
            onAddChapterSuccess(response);
          }
        );
      } else {
        getUpdateChapter({
          ...chapterData,
          id: values.id,
          screenshot: values.screenshot,
        });
      }
    },
  });

  // The trimmer keeps start_time/end_time as the source of truth for the
  // selected range, so measure the span from those and fall back to the stored
  // duration for chapters that predate a trim.
  const clipDuration =
    formik.values.end_time - formik.values.start_time ||
    formik.values.duration ||
    0;
  const isClipTooShort = clipDuration < MIN_CLIP_DURATION;
  // Nothing valid can be trimmed out of media that is itself under the floor,
  // so the create entry points are closed rather than opening a doomed sheet.
  const mediaDuration = currentMediaDetailsForEdit?.meta?.duration || 0;
  const isMediaTooShort = mediaDuration < MIN_CLIP_DURATION;
  // Clips are trimmed from the active/live source video, so creation is
  // blocked until the media has actually reached that state (state 10 / status 5).
  const isMediaNotReady =
    currentMediaDetailsForEdit?.state !== 10 ||
    currentMediaDetailsForEdit?.status !== 5;

  const newChapter = {
    id: "new_chapter",
    title: "",
    description: "",
    duration: currentMediaDetailsForEdit?.meta?.duration || 0,
    end_time: currentMediaDetailsForEdit?.meta?.duration || 0,
    start_time: 0,
    thumb: currentMediaDetailsForEdit?.images?.thumb,
    base: currentMediaDetailsForEdit?.images?.base,
    base64: [],
    media: currentMediaDetailsForEdit.id,
    poster: "",
    ct: 0,
    mt: 0,
    start: 0,
    clip_id: undefined,
  };
  const defaultThumbUrl = `${currentMediaDetailsForEdit?.images?.base}/${
    currentMediaDetailsForEdit?.images?.thumb
  }${currentMediaDetailsForEdit?.source === "dbsync" ? ".jpg" : "_180.jpg"}`;

  const initialFormikValues = {
    title: `Clip ${(chapters?.length || 0) + 1}`,
    description: "",
    duration: currentMediaDetailsForEdit?.meta?.duration || 0,
    end_time: currentMediaDetailsForEdit?.meta?.duration || 0,
    start_time: 0,
    thumb: defaultThumbUrl,
    thumbId: currentMediaDetailsForEdit?.images?.thumb,
    base64: [],
    id: "new_chapter",
    clip_id: undefined,
    tags: [],
  };

  useEffect(() => {
    if (currentOpenedChapter) {
      startTrimRef.current = currentOpenedChapter.start_time || 0;
      endTrimRef.current =
        currentOpenedChapter.end_time ||
        currentMediaDetailsForEdit?.meta?.duration ||
        0;
      durationRef.current =
        currentOpenedChapter.duration ||
        currentMediaDetailsForEdit?.meta?.duration ||
        0;
      formik.setValues(
        {
          ...formik.values,
          start_time: currentOpenedChapter.start_time || 0,
          end_time:
            currentOpenedChapter.end_time ||
            currentMediaDetailsForEdit?.meta?.duration ||
            0,
          duration:
            currentOpenedChapter.duration ||
            currentMediaDetailsForEdit?.meta?.duration ||
            0,
        },
        false
      );
    }
  }, [currentOpenedChapter]);

  useEffect(() => {
    const compareFormValues = (
      initialValues: ChapterInterface,
      currentValues: MediaChapterForm
    ) => {
      // console.warn(initialValues, currentValues, 'initialValues, currentValues');
      const changedKeys = Object.keys(currentValues).filter((key) => {
        if (
          key === "thumb" ||
          key === "thumbId" ||
          key === "clip_id" ||
          key === "clip_start" ||
          key === "clip_end" ||
          key === "base64"
        )
          return false;
        const currentValue = currentValues[key as keyof MediaChapterForm];
        const initialValue = initialValues[key as keyof ChapterInterface];

        if (Array.isArray(currentValue) || Array.isArray(initialValue)) {
          return (
            JSON.stringify(currentValue || []) !==
            JSON.stringify(initialValue || [])
          );
        }
        return currentValue !== initialValue;
      });
      const shouldIgnoreThumbComparison =
        (currentValues.screenshot && currentValues.screenshot !== 0) ||
        (initialValues.screenshot && initialValues.screenshot !== 0);

      if (shouldIgnoreThumbComparison) {
        if (currentValues.screenshot && currentValues.screenshot !== 0) {
          if (currentValues.screenshot !== initialValues.screenshot) {
            changedKeys.push("screenshot");
          }
        }
      } else {
        const thumbChanged = currentValues.thumbId !== initialValues.thumb;
        if (thumbChanged) changedKeys.push("thumb/thumbId");
      }
      // console.warn(changedKeys, 'changedKeys');
      return changedKeys;
    };

    // console.warn(compareFormValues(currentOpenedChapter, formik.values), 'compareFormValues');
    if (currentChapter === "new_chapter") {
      const changedFields = compareFormValues(
        initialFormikValues as unknown as ChapterInterface,
        formik.values
      );
      setIsFormDirty1(changedFields.length > 0);
    } else if (currentOpenedChapter) {
      const changedFields = compareFormValues(
        currentOpenedChapter,
        formik.values
      );
      // console.warn('Changed fields:', changedFields);
      setIsFormDirty1(changedFields.length > 0);
    }
  }, [formik.values, currentOpenedChapter, currentChapter]);

  const getThumbnailUrl = (chapter: ChapterInterface) => {
    if (chapter.base64 && chapter.base64.length > 0) {
      if (lastTempThumb[chapter.id]?.startsWith("blob:")) {
        return lastTempThumb[chapter.id];
      }
      return chapter.base64[0];
    }
    if (chapter.thumb) {
      const base = chapter.base || currentMediaDetailsForEdit?.images?.base;
      return `${base}/${chapter.thumb}_180.jpg`;
    }
    return `${currentMediaDetailsForEdit?.images?.base}/${
      currentMediaDetailsForEdit?.images?.thumb
    }${currentMediaDetailsForEdit?.source === "dbsync" ? ".jpg" : "_180.jpg"}`;
  };

  const handleTimeUpdate = (time: number) => {
    setCurrentPlayerTime(time);
  };

  const handleStartEndTrimChange = (start: number, end: number) => {
    startTrimRef.current = start;
    endTrimRef.current = end;
    durationRef.current = end - start;
    setHasTimeChanged(true);
    setIsFormDirty1(true);
    setTimeout(() => {
      formik.setFieldValue("start_time", start, false);
      formik.setFieldValue("end_time", end, false);
      formik.setFieldValue("duration", end - start, false);
    }, 0);
  };

  const setChapterData = (chapter: ChapterInterface) => {
    const existingThumb = lastTempThumb[chapter.id];
    const thumbUrl = existingThumb?.startsWith("blob:")
      ? existingThumb
      : getThumbnailUrl(chapter);
    setLastTempThumb((prev) => {
      if (prev[chapter.id]?.startsWith("blob:")) {
        return prev;
      }
      if (prev[chapter.id] === thumbUrl) {
        return prev;
      }
      return {
        ...prev,
        [chapter.id]: thumbUrl,
      };
    });
    setHasTimeChanged(false);
    formik.setValues({
      title: chapter.title,
      description: chapter.description,
      duration: chapter.duration,
      end_time: chapter.end_time,
      start_time: chapter.start_time,
      thumb: thumbUrl,
      thumbId: chapter.thumb || currentMediaDetailsForEdit?.images?.thumb,
      base64: chapter.base64 || [],
      id: chapter.id,
      clip_id: chapter.clip_id || undefined,
      tags: chapter?.tags || [],
    });
    durationRef.current = chapter.duration;
    startTrimRef.current = chapter.start_time;
    endTrimRef.current = chapter.end_time;
  };

  useEffect(() => {
    if (
      !isLoadingChapters &&
      chapters &&
      Object.keys(lastTempThumb).length === 0
    ) {
      const thumbUpdates = chapters.reduce(
        (acc: any, chapter: any) => {
          acc[chapter.id] = getThumbnailUrl(chapter);
          return acc;
        },
        {} as Record<string, string>
      );
      setLastTempThumb(thumbUpdates);
      if (chapters.length > 0 && !currentChapter) {
        const firstChapter = chapters[0];
        setChapterData(firstChapter);
        setCurrentChapter(firstChapter.id);
        setCurrentOpenedChapter(firstChapter);
      } else if (chapters.length === 0 && !currentChapter) {
        // console.warn('chapters.length === 0 && !currentChapter', chapters, currentChapter);
        handleAddChapter();
      }
    }
  }, [chapters, isLoadingChapters, currentChapter]);

  const handleAddChapter = (openSheet = false) => {
    setTempChapter(null);
    setPlayerKey((prev) => prev + 1);
    formik.resetForm();
    setCurrentChapter("new_chapter");
    setTempChapter(newChapter);
    setCurrentOpenedChapter(newChapter);
    setHasTimeChanged(false);
    setLastTempThumb((prev) => {
      const { new_chapter: _, ...rest } = prev;
      return rest;
    });
    formik.setValues(initialFormikValues, false);
    if (openSheet) {
      setIsSheetOpen(true);
    }
  };

  const onAddChapterSuccess = (response: any) => {
    const id = response?.id;
    const clip_id = response?.clip_id || undefined;
    const currentThumb = lastTempThumb[currentChapter];
    const currentThumbId = formik.values.thumbId;
    const currentBase = currentMediaDetailsForEdit?.images?.base;
    const currentThumbUrl = formik.values.thumb;

    setCurrentChapter(id);
    formik.setValues({
      ...formik.values,
      id: id,
      thumb: currentThumbUrl,
      thumbId: currentThumbId,
      base64: formik.values.base64,
      clip_id: clip_id || undefined,
    });
    if (currentOpenedChapter) {
      const updatedChapter = {
        ...currentOpenedChapter,
        description: formik.values.description,
        id: id,
        clip_id: clip_id || undefined,
        start_time: startTrimRef.current,
        end_time: endTrimRef.current,
        duration: durationRef.current,
      };
      setCurrentOpenedChapter(updatedChapter);
    }

    setTempChapter(null);
    if (currentThumb) {
      setLastTempThumb((prev) => ({
        ...prev,
        [id]: currentThumbUrl,
      }));
    }
    setIsSheetOpen(false);
  };

  function extractIdFromUrl(url: string) {
    if (!url) return null;
    const parts = url.split("/").pop();
    const id = parts?.split("_")[0];
    return id;
  }

  const handleEditChapter = (id: string) => {
    setIsSheetOpen(true);
    if (id === "new_chapter" && currentChapter !== "new_chapter") {
      return;
    }

    setPlayerKey((prev) => prev + 1);
    if (id !== "new_chapter" && tempChapter) {
      setTempChapter(null);
      setLastTempThumb((prev) => {
        const { new_chapter: _, ...rest } = prev;
        return rest;
      });
    }
    if (id === "new_chapter") {
      if (currentChapter === "new_chapter") {
        return;
      }
      formik.resetForm();
      setCurrentChapter("new_chapter");
      setCurrentOpenedChapter(newChapter);
      setTempChapter(newChapter);
      startTrimRef.current = 0;
      endTrimRef.current = currentMediaDetailsForEdit?.meta?.duration || 0;
      durationRef.current = currentMediaDetailsForEdit?.meta?.duration || 0;
      formik.setValues(initialFormikValues, false);
      return;
    }
    const chapter = chapters.find((c: any) => c.id === id);
    if (chapter) {
      setCurrentChapter(id);
      startTrimRef.current = chapter.start_time || 0;
      endTrimRef.current =
        chapter.end_time || currentMediaDetailsForEdit?.meta?.duration || 0;
      durationRef.current =
        chapter.duration || currentMediaDetailsForEdit?.meta?.duration || 0;
      setCurrentOpenedChapter(chapter);
      const existingThumb = lastTempThumb[chapter.id];
      const thumbUrl = existingThumb?.startsWith("blob:")
        ? existingThumb
        : chapter.base64?.length > 0
          ? chapter.base64[0]
          : chapter.thumb
            ? `${chapter.base || currentMediaDetailsForEdit?.images?.base}/${
                chapter.thumb
              }${
                currentMediaDetailsForEdit?.source === "dbsync"
                  ? ".jpg"
                  : "_180.jpg"
              }`
            : `${currentMediaDetailsForEdit?.images?.base}/${
                currentMediaDetailsForEdit?.images?.thumb
              }${
                currentMediaDetailsForEdit?.source === "dbsync"
                  ? ".jpg"
                  : "_180.jpg"
              }`;
      if (existingThumb?.startsWith("blob:") || chapter.base64?.length > 0) {
        setLastTempThumb((prev) => ({
          ...prev,
          [chapter.id]: thumbUrl,
        }));
      }
      formik.setValues(
        {
          title: chapter.title || "",
          description: chapter.description || "",
          duration:
            chapter.duration || currentMediaDetailsForEdit?.meta?.duration || 0,
          end_time:
            chapter.end_time || currentMediaDetailsForEdit?.meta?.duration || 0,
          start_time: chapter.start_time || 0,
          thumb: thumbUrl,
          thumbId:
            chapter.thumb || currentMediaDetailsForEdit?.images?.thumb || "",
          base64: chapter.base64 || [],
          id: chapter.id,
          clip_id: chapter.clip_id || undefined,
          clip_start: chapter.clip_start,
          clip_end: chapter.clip_end,
          tags: chapter?.tags || [],
        },
        false
      );
      setHasTimeChanged(false);
    }
  };

  const handleDeleteChapter = (id: string) => {
    if (id === "new_chapter") {
      formik.setValues(initialFormikValues, false);
      return;
    }
    setDeleteChapterId(id);
  };

  const confirmDeleteChapter = () => {
    const id = deleteChapterId;
    setDeleteChapterId(null);
    if (!id) return;

    getDeleteChapter(id);
    setIsSheetOpen(false);

    const updatedChapters = chapters?.filter(
      (chapter: any) => chapter.id !== id
    );
    if (updatedChapters.length > 0) {
      if (currentChapter === id) {
        setChapterData(updatedChapters[0]);
        setCurrentChapter(updatedChapters[0].id);
        setCurrentOpenedChapter(updatedChapters[0]);
      }
    } else {
      setCurrentChapter("");
      setCurrentOpenedChapter(null);
      setTempChapter(null);
    }
  };

  const handleScreenshot = (blobURL: string, base64Img: string) => {
    const thumbId = extractIdFromUrl(blobURL) || "";
    formik.setValues(
      {
        ...formik.values,
        base64: [base64Img],
        thumb: blobURL,
        thumbId: thumbId,
        screenshot: currentPlayerTime,
      },
      true
    );
    if (currentChapter) {
      setLastTempThumb((prev) => ({
        ...prev,
        [currentChapter]: blobURL,
      }));
    }
    if (currentOpenedChapter) {
      const updatedChapter = {
        ...currentOpenedChapter,
        base64: [base64Img],
        thumb: blobURL,
        thumbId: thumbId,
        start_time: startTrimRef.current,
        end_time: endTrimRef.current,
        duration: durationRef.current,
      };
      setCurrentOpenedChapter(updatedChapter);
    }
    formik.validateForm();
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    formik.handleChange(e);
    if (currentChapter === "new_chapter" && tempChapter) {
      const updatedTempChapter = {
        ...tempChapter,
        title: newTitle,
        start_time: startTrimRef.current,
        end_time: endTrimRef.current,
        duration: durationRef.current,
      };
      setTempChapter(updatedTempChapter);
    } else {
      const updatedChapter = {
        ...currentOpenedChapter,
        start_time: startTrimRef.current,
        end_time: endTrimRef.current,
        duration: durationRef.current,
      };
      setCurrentOpenedChapter(updatedChapter);
      const updatedChapters = chapters.map((chapter: any) =>
        chapter.id === currentChapter
          ? {
              ...chapter,
              start_time: startTrimRef.current,
              end_time: endTrimRef.current,
              duration: durationRef.current,
            }
          : chapter
      );
      dispatch(
        setMediaChapters({
          data: {
            list: updatedChapters,
            count: updatedChapters.length,
            mediaId: currentMediaDetailsForEdit.id,
          },
        })
      );
    }
  };

  const handleSheetCancel = () => {
    if (isFormDirty1) {
      setShowCloseConfirm(true);
      return;
    }
    closeSheet();
  };

  const closeSheet = () => {
    setIsSheetOpen(false);
    if (currentChapter === "new_chapter") {
      setTempChapter(null);
      setLastTempThumb((prev) => {
        const { new_chapter: _, ...rest } = prev;
        return rest;
      });
      if (chapters && chapters.length > 0) {
        const firstChapter = chapters[0];
        setChapterData(firstChapter);
        setCurrentChapter(firstChapter.id);
        setCurrentOpenedChapter(firstChapter);
      }
    }
  };

  const handleCustomPromptClick = (field: "title" | "description") => {
    setCustomPrompt((prev) => ({
      ...prev,
      [field]: { ...prev[field], open: true },
    }));
  };

  const handleCustomPromptClose = (field: "title" | "description") => {
    setCustomPrompt((prev) => ({
      ...prev,
      [field]: { open: false, text: "" },
    }));
  };

  const handleCustomPromptTextChange = (
    field: "title" | "description",
    text: string
  ) => {
    setCustomPrompt((prev) => ({
      ...prev,
      [field]: { ...prev[field], text },
    }));
  };

  const handlePromptSend = async (field: "title" | "description") => {
    if (!customPrompt[field].text.trim() || customPrompt[field].isSending)
      return;

    setCustomPrompt((prev) => ({
      ...prev,
      [field]: { ...prev[field], isSending: true },
    }));

    try {
      const response = await dispatch(
        aiService({
          title: formik.values[field],
          id: currentMediaDetailsForEdit.id,
          service: "prompt",
          target_language: customPrompt[field].text,
        })
      );

      if (response) {
        if (field === "title") {
          formik.setFieldValue("title", response.output_processed.output || "");
        } else {
          formik.setFieldValue(
            "description",
            response.output_processed.output || ""
          );
        }
        setCustomPrompt((prev) => ({
          ...prev,
          [field]: { ...prev[field], text: "", isSending: false },
        }));
      }
    } catch (error) {
      console.error("Error in AI service:", error);
      setCustomPrompt((prev) => ({
        ...prev,
        [field]: { ...prev[field], text: "", isSending: false },
      }));
    }
  };

  useEffect(() => {
    const urlMediaId = location.pathname.split("/")[2];
    if (urlMediaId) {
      getListChapters(urlMediaId);
      getAssets({ pid: urlMediaId });
    }
    return () => {
      resetMediaChapters();
    };
  }, [location.pathname, resetMediaChapters]);

  useEffect(() => {
    if (currentOpenedChapter?.clip_id) {
      setIsLoadingChildMedia(true);
      GetMediaApi({ id: currentOpenedChapter.clip_id })
        .then((response) => {
          if (response.data) {
            setChildMediaData(response.data);
          }
        })
        .finally(() => {
          setIsLoadingChildMedia(false);
        });
    } else {
      setChildMediaData(null);
    }
  }, [currentOpenedChapter?.clip_id]);

  const allChapters = tempChapter
    ? [
        tempChapter,
        ...(chapters?.filter((c: any) => c.id !== "new_chapter") || []),
      ]
    : [...(chapters?.filter((c: any) => c.id !== "new_chapter") || [])];

  useEffect(() => {
    if (
      currentOpenedChapter?.clip_start !== undefined &&
      currentOpenedChapter?.clip_end !== undefined
    ) {
      const startTimeMatches =
        currentOpenedChapter.clip_start === currentOpenedChapter.start_time;
      const endTimeMatches =
        currentOpenedChapter.clip_end === currentOpenedChapter.end_time;
      setHasTimeChanged(!(startTimeMatches && endTimeMatches));
    }
  }, [currentOpenedChapter]);

  if (isLoadingChapters) {
    return (
      <div className="w-100 px-3 py-3">
        <Skeleton type="title" width="160px" />
        <div className="d-flex gap-3 w-100 flex-md-row flex-column mt-3">
          <div
            className="border-end d-flex flex-column"
            style={{
              maxWidth: "160px",
              flex: "0 0 160px",
              paddingRight: "15px",
            }}
          >
            <Skeleton type="text" height={38} />
            <Skeleton type="thumbnail" height={90} />
            <Skeleton type="thumbnail" height={90} />
            <Skeleton type="thumbnail" height={90} />
          </div>
          <div className="flex-grow-1" style={{ maxWidth: "920px" }}>
            <div className="row border rounded g-0">
              <div className="col col-8 p-3">
                <Skeleton type="thumbnail" height={320} />
              </div>
              <div className="col col-4 p-3">
                <Skeleton type="text" height={42} />
                <Skeleton type="text" height={110} />
                <Skeleton type="text" height={42} />
                <Skeleton type="text" height={110} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main_form p-3">
      <SectionHeader
        title="Clips"
        subtitle="Create and manage clips from this video"
      >
        {chapters?.length !== 0 && (
          <Dropdown>
            <Dropdown.Toggle
              variant="primary"
              id="create-chapter-dropdown"
              disabled={isMediaTooShort || isMediaNotReady}
              title={
                isMediaNotReady
                  ? "Available once the video has finished processing."
                  : isMediaTooShort
                    ? `This video is shorter than the ${MIN_CLIP_DURATION}s minimum clip length.`
                    : undefined
              }
            >
              Create New
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              <Dropdown.Item
                onClick={() => handleAddChapter(true)}
                disabled={isMediaTooShort || isMediaNotReady}
              >
                Create New
              </Dropdown.Item>
              <Dropdown.Item disabled>Import from Chapters</Dropdown.Item>
              <Dropdown.Item disabled>Create with AI</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </SectionHeader>

      {chapters?.length === 0 ? (
        <div
          className="d-flex flex-column align-items-center justify-content-center gap-3 rounded-3"
          style={{ minHeight: "60vh", backgroundColor: "#f1f1f1" }}
        >
          <Scissors size={40} className="text-muted" strokeWidth={1.5} />
          <span className="text-muted mb-0">Create clips from video</span>
          <Button
            type="primary"
            onClick={() => handleAddChapter(true)}
            isDisabled={isMediaTooShort || isMediaNotReady}
          >
            Create
          </Button>
          {isMediaNotReady ? (
            <span className="text-danger small">
              Available once the video has finished processing.
            </span>
          ) : (
            isMediaTooShort && (
              <span className="text-danger small">
                {`This video is shorter than the ${MIN_CLIP_DURATION}s minimum clip length.`}
              </span>
            )
          )}
        </div>
      ) : (
        <div className="pt-3 row gy-3">
          <AnimatePresence>
            {allChapters
              .filter((chapter) => chapter.id !== "new_chapter")
              .map((chapter) => {
                const existingThumb = lastTempThumb[chapter.id];
                const thumbnailUrl = existingThumb?.startsWith("blob:")
                  ? existingThumb
                  : getThumbnailUrl(chapter);
                return (
                  <div key={chapter.id} className="col-sm-12 col-md-4 col-lg-4">
                    <AddedChapterThumbnail
                      id={chapter.id}
                      description={chapter.description}
                      duration={chapter.duration}
                      startTime={chapter.start_time}
                      endTime={chapter.end_time}
                      isGenerated={!!(chapter.clip_id && chapter.enable)}
                      thumbnailUrl={thumbnailUrl}
                      title={
                        chapter.id === currentChapter &&
                        formik.values.title !== ""
                          ? formik.values.title
                          : chapter.title || "Untitled"
                      }
                      handleDelete={handleDeleteChapter}
                      handleEdit={() => {
                        handleEditChapter(chapter.id);
                      }}
                      handleGenerate={() => generateClip({ id: chapter.id })}
                      isSelected={chapter.id === currentChapter}
                    />
                  </div>
                );
              })}
          </AnimatePresence>
        </div>
      )}

      <Offcanvas
        show={isSheetOpen}
        onHide={handleSheetCancel}
        placement="end"
        backdrop
        // ThumbnailSelection's Modal and the close-confirm AlertDialog both
        // portal to document.body while this sheet stays open - react-bootstrap's
        // default enforceFocus doesn't know about them and fights to yank focus
        // back into the sheet, breaking inputs/dropdowns in those dialogs (same
        // issue fixed on SocialPublish.tsx's Offcanvas).
        enforceFocus={false}
        style={{ width: "min(1024px, 100vw)" }}
      >
        <Offcanvas.Header>
          <Offcanvas.Title className="fs-8 text-uppercase">
            {currentChapter === "new_chapter" ? "Create Clip" : "Edit Clip"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body
          style={{
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            paddingTop: 0,
          }}
        >
          <div className="row px-2 border">
            <div className="col col-9 d-flex flex-column gap-2 pb-3">
              <div style={{ borderRadius: 10, overflow: "hidden" }}>
                <GeneralPlayer
                  key={playerKey}
                  ref={generalPlayerRef}
                  id={currentMediaDetailsForEdit.id}
                  controls={false}
                  startTrim={0}
                  endTrim={currentMediaDetailsForEdit?.meta?.duration || 0}
                  onTimeUpdate={handleTimeUpdate}
                  onPlayStateChange={setIsPlayerPaused}
                  onImageReel={setImageReel}
                  videos={videos}
                  autoPlay={3}
                  onScreenshot={handleScreenshot}
                />
              </div>
              <VideoRangeTrimmer
                posterUrl={formik.values.thumb || noImage}
                duration={currentMediaDetailsForEdit?.meta?.duration || 0}
                start={formik.values.start_time}
                end={formik.values.end_time}
                onChange={handleStartEndTrimChange}
                playerRef={generalPlayerRef}
                currentTime={currentPlayerTime}
                isPaused={isPlayerPaused}
                imageReel={imageReel}
                minDuration={MIN_CLIP_DURATION}
              />
            </div>
            <div className="col col-3 p-0 d-flex flex-column justify-content-between bg-light h-100">
              <div className="row gap-3 p-2">
                <div className="col col-md-12">
                  {customPrompt.title.open && (
                    <TextArea
                      name="prompt"
                      value={customPrompt.title.text}
                      onChange={(e: any) => {
                        handleCustomPromptTextChange("title", e.target.value);
                      }}
                      onBlur={() => {}}
                      label="Custom Prompt"
                      showRequired={false}
                      error={
                        customPrompt.title.text.length > 500
                          ? "Prompt should be less than 500 characters"
                          : undefined
                      }
                      touched={customPrompt.title.text.length > 500}
                      placeholder="Enter your prompt here..."
                      characterLimit={500}
                      onSend={() => handlePromptSend("title")}
                      isSending={customPrompt.title.isSending}
                      style={{
                        marginBottom: "1rem",
                        backgroundColor: "#fff",
                      }}
                    />
                  )}
                  <TextInput
                    label="Title"
                    name="title"
                    onChange={handleTitleChange}
                    value={formik.values.title}
                    characterLimit={100}
                    onBlur={formik.handleBlur}
                    error={formik.errors.title}
                    touched={formik.touched.title}
                    showRequired={true}
                    rightElement={
                      <AIAssistButtons
                        isOpen={activeField === "title"}
                        onToggle={() =>
                          setActiveField(
                            activeField === "title" ? null : "title"
                          )
                        }
                        field="title"
                        textToProcess={formik.values.title}
                        mediaId={currentMediaDetailsForEdit.id}
                        onResponseReceived={(response: any) =>
                          formik.setFieldValue("title", response)
                        }
                        position="right"
                        onCustomPromptClick={() =>
                          handleCustomPromptClick("title")
                        }
                        customPromptOpen={customPrompt.title.open}
                        onCustomPromptClose={() =>
                          handleCustomPromptClose("title")
                        }
                      />
                    }
                  />
                </div>
                <div className="col col-md-12">
                  {customPrompt.description.open && (
                    <TextArea
                      label="Custom Prompt"
                      name="customPromptDesc"
                      showRequired={false}
                      placeholder="Enter your custom prompt..."
                      value={customPrompt.description.text}
                      onBlur={() => {}}
                      onChange={(e: any) =>
                        handleCustomPromptTextChange(
                          "description",
                          e.target.value
                        )
                      }
                      error={
                        customPrompt.description.text.length > 500
                          ? "Prompt should be less than 500 characters"
                          : undefined
                      }
                      touched={customPrompt.description.text.length > 500}
                      characterLimit={500}
                      onSend={() => handlePromptSend("description")}
                      isSending={customPrompt.description.isSending}
                      style={{
                        marginBottom: "1rem",
                        backgroundColor: "#fff",
                      }}
                    />
                  )}
                  <TextArea
                    label="Description"
                    name="description"
                    onChange={formik.handleChange}
                    showRequired={true}
                    value={formik.values.description}
                    characterLimit={5000}
                    placeholder=""
                    onBlur={formik.handleBlur}
                    style={{ resize: "none" }}
                    error={formik.errors.description}
                    touched={formik.touched.description}
                    fullHeight={false}
                    rightElement={
                      <AIAssistButtons
                        isOpen={activeField === "description"}
                        onToggle={() =>
                          setActiveField(
                            activeField === "description" ? null : "description"
                          )
                        }
                        field="description"
                        textToProcess={formik.values.description}
                        mediaId={currentMediaDetailsForEdit.id}
                        onResponseReceived={(response: any) =>
                          formik.setFieldValue("description", response)
                        }
                        position="right"
                        onCustomPromptClick={() =>
                          handleCustomPromptClick("description")
                        }
                        customPromptOpen={customPrompt.description.open}
                        onCustomPromptClose={() =>
                          handleCustomPromptClose("description")
                        }
                      />
                    }
                  />
                </div>
                <div className="col col-md-12 H">
                  <TextInput
                    label="Screenshot"
                    name="screenshot"
                    value={formik.values.screenshot || 0}
                    characterLimit={100}
                    onBlur={formik.handleBlur}
                    error={formik.errors.screenshot}
                    touched={formik.touched.screenshot}
                    showRequired={true}
                    displaynone={true}
                    onChange={(e: any) => {
                      formik.setFieldValue(
                        "screenshot",
                        Number(e.target.value)
                      );
                    }}
                  />
                </div>

                <div className="col col-md-12">
                  <ChipInput
                    label="Tags"
                    value={formik.values.tags}
                    placeholder="Add tag"
                    totalCharacterLimit={500}
                    fixedLayout
                    handleChipChange={(chip, action) => {
                      if (action === "add") {
                        formik.setFieldValue("tags", [
                          ...formik.values.tags,
                          ...(Array.isArray(chip) ? chip : [chip]),
                        ]);
                      } else if (action === "remove") {
                        formik.setFieldValue(
                          "tags",
                          formik.values.tags.filter((t) => t !== chip)
                        );
                      } else if (action === "clear") {
                        formik.setFieldValue("tags", []);
                      }
                    }}
                  />
                </div>
                <div className="col col-md-12">
                  <label className="form-label">Thumbnail</label>
                  <div className="position-relative" style={{ width: "170px" }}>
                    <ThumbnailSelection
                      baseUrl={generateBaseUrl(currentMediaDetailsForEdit)}
                      assets={mediaAssets}
                      handleThumbnailChange={(
                        newThumbnailUrl: any,
                        id: any
                      ) => {
                        const newUrl = `${generateBaseUrl(
                          currentMediaDetailsForEdit
                        )}/${id}${
                          currentMediaDetailsForEdit?.source === "dbsync"
                            ? ".jpg"
                            : "_180.jpg"
                        }`;
                        formik.setFieldValue("thumb", newUrl);
                        formik.setFieldValue("thumbId", id);
                        formik.setFieldValue("base64", []);
                        setLastTempThumb((prev) => ({
                          ...prev,
                          [currentChapter]: newUrl,
                        }));
                      }}
                      currentThumbnail={
                        formik.values.base64?.length > 0
                          ? formik.values.base64[0]
                          : formik.values.thumb
                      }
                      currentThumbnailId={formik.values.thumbId}
                      disabled={false}
                      error={formik.errors.thumbId}
                      touched={formik.touched.thumbId}
                      updateModal={updateModal}
                      isWizard={false}
                    />
                    {formik.errors.thumb && <div>{formik.errors.thumb}</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {currentOpenedChapter?.clip_id && currentOpenedChapter?.enable && (
            <div className="pointer d-flex align-items-start p-3 gap-2 border rounded flex-column mt-3">
              {isLoadingChildMedia ? (
                <div className="w-100">
                  <Skeleton type="text" width="160px" height={20} />
                  <div className="d-flex gap-3 align-items-center w-100 mt-2">
                    <Skeleton type="thumbnail" width={130} height={76} />
                    <div className="flex-grow-1">
                      <Skeleton type="text" height={18} />
                      <Skeleton type="text" width="70%" height={16} />
                    </div>
                  </div>
                </div>
              ) : childMediaData ? (
                <>
                  <span className="w-100 pb-2 border-bottom">
                    GENERATED CLIP
                  </span>
                  <div className="d-flex gap-3 pointer align-items-center justify-content-between w-100">
                    <div
                      className="d-flex gap-3 py-2"
                      onClick={() => {
                        navigate(
                          `/media/${currentOpenedChapter?.clip_id}/details`,
                          {
                            state: {
                              fromChapter: true,
                              mediaId: currentMediaDetailsForEdit?.id,
                              chapterId: currentChapter,
                              returnPath: `/media/${currentMediaDetailsForEdit?.id}/clips/${currentChapter}`,
                            },
                          }
                        );
                      }}
                    >
                      <div className="position-relative">
                        <div
                          className="rounded ratio ratio-16x9 border bg-secondary pointer"
                          style={{ width: "130px", minWidth: "130px" }}
                        >
                          <img
                            src={
                              childMediaData.images?.thumb
                                ? `${generateBaseUrl(childMediaData)}${
                                    childMediaData.images.thumb
                                  }${
                                    childMediaData?.source === "dbsync"
                                      ? ".jpg"
                                      : "_180.jpg"
                                  }`
                                : noImage
                            }
                            alt={childMediaData.title}
                            className="object-fit-contain position-absolute top-50 start-50 translate-middle"
                          />
                        </div>
                        <span
                          className="position-absolute bottom-0 end-0 bg-dark text-light px-1 fs-6"
                          style={{ bottom: -1, right: -1 }}
                        >
                          {secondsToHms(childMediaData.meta?.duration) || 0}
                        </span>
                      </div>
                      <div className="d-flex flex-column gap-1 flex-wrap">
                        <span className="d-flex flex-wrap text-break text-primary">
                          {childMediaData.title || "Untitled"}
                        </span>
                        <span className="d-flex flex-wrap text-break text-secondary">
                          {childMediaData.desc || "No description"}
                        </span>
                        {childMediaData.status === 5 &&
                        childMediaData.state === 10 ? (
                          <Tooltip content="Copy ID">
                            <div
                              className={`pointer rounded badge border d-flex gap-1 text-capitalize fs-6 font-weight-normal`}
                              style={{
                                padding: "6px 8px",
                                color: "#fff",
                                backgroundColor: "#999",
                              }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                await copyTextToClipboard(childMediaData.id);
                                toast.success("Slike ID copied");
                              }}
                            >
                              {childMediaData.id}
                            </div>
                          </Tooltip>
                        ) : (
                          <div className={`d-flex align-items-center `}>
                            <div
                              className={`${
                                fileProcessingStatusToTextAndColor(
                                  childMediaData?.state
                                ).statusColor
                              } badge`}
                            >
                              {`${
                                fileProcessingStatusToTextAndColor(
                                  childMediaData?.state
                                ).statusText
                              }`}
                            </div>
                            <span
                              className="position-absolute"
                              style={{ right: "-18px", top: "4px" }}
                            ></span>
                          </div>
                        )}
                      </div>
                    </div>
                    {hasTimeChanged && !isFormDirty1 && (
                      <div>
                        <Button
                          type="outline-primary"
                          onClick={(e: any) => {
                            e.preventDefault();
                            generateClip({
                              id: currentOpenedChapter?.id,
                            });
                          }}
                        >
                          Regenerate
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-muted">No child media data available</div>
              )}
            </div>
          )}
        </Offcanvas.Body>
        <div className="d-flex flex-wrap align-items-center px-3 py-2 border-top text-muted kbd-shortcut-row">
          <b className="me-1">Shortcuts :</b>
          <span>
            <b>1 frame -</b>{" "}
            <kbd>
              <ArrowLeft size={11} />
            </kbd>
            /
            <kbd>
              <ArrowRight size={11} />
            </kbd>
          </span>
          <span>
            <b>1 sec -</b> <kbd>Shift</kbd>+
            <kbd>
              <ArrowLeft size={11} />
            </kbd>
            /
            <kbd>
              <ArrowRight size={11} />
            </kbd>
          </span>
          <span>
            <b>10 sec -</b> <kbd>Ctrl</kbd>/<kbd>Option</kbd> +
            <kbd>
              <ArrowLeft size={11} />
            </kbd>
            /
            <kbd>
              <ArrowRight size={11} />
            </kbd>
          </span>
          <span>
            <b>Jump to Start -</b>{" "}
            <kbd>
              <ArrowUp size={11} />
            </kbd>
          </span>
          <span>
            <b>Jump to End -</b>{" "}
            <kbd>
              <ArrowDown size={11} />
            </kbd>
          </span>
          <span>
            <b>Play/Pause -</b> <kbd>Space</kbd>
          </span>
        </div>
        <div className="d-flex justify-content-end align-items-center gap-2 p-3 border-top bg-white">
          <Button type="outline-primary" onClick={handleSheetCancel}>
            Cancel
          </Button>
          <span
            className="text-danger small me-auto"
            style={{ visibility: isClipTooShort ? "visible" : "hidden" }}
          >
            {`A clip must be at least ${MIN_CLIP_DURATION} seconds long.`}
          </span>
          {currentChapter !== "new_chapter" && (
            <Button
              onClick={() => handleDeleteChapter(formik.values.id)}
              type="outline-primary"
            >
              DELETE
            </Button>
          )}
          <Button
            type="primary"
            onClick={() => {
              formik.handleSubmit();
              if (currentChapter !== "new_chapter") {
                setIsSheetOpen(false);
              }
            }}
            isDisabled={!isFormDirty1 || isClipTooShort}
          >
            {currentChapter === "new_chapter" ? "Create" : "Save"}
          </Button>
        </div>
      </Offcanvas>
      <AlertDialog
        isOpen={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        onConfirm={closeSheet}
        title="ALERT!"
        message="Are you sure you want to close without saving?"
        confirmText="Yes"
        cancelText="No"
        confirmButtonType="warning"
      />
      <AlertDialog
        isOpen={!!deleteChapterId}
        onClose={() => setDeleteChapterId(null)}
        onConfirm={confirmDeleteChapter}
        title="ALERT!"
        message="Do you really want to delete this clip."
        confirmText="Yes"
        cancelText="No"
        confirmButtonType="warning"
      />
    </div>
  );
};

const mapState = (state: RootState) => ({
  currentMediaDetailsForEdit: state.media.mediaDetails.media,
  softRefresh: state.app.softRefresh,
  chapters: state.media.mediaChapters.list,
  isLoadingMediaDetails: state.media.mediaDetails.isLoading,
  isLoadingChapters: state.media.mediaChapters.isLoading,
  isDeletingChapter: state.media.mediaChapters.isDeletingChapter,
  isSavingChapter: state.media.mediaChapters.isSavingChapter,
  mediaAssets: state.media.mediaAssets.assets,
  currentOpenedChapter: state.media.mediaChapters.currentOpenedChapter,
});

const mapDispatch = {
  getAddChapter,
  getDeleteChapter,
  getListChapters,
  getUpdateChapter,
  resetMediaChapters,
  getAssets,
  setCurrentOpenedChapter,
  generateClip,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;
export default connector(MediaChapter);
